"use client";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/custom-api.ts";
import Loader from "../components/loader.tsx";
import { materialTypeReverseMapper } from "../utils/mappers/material-type.mapper.ts";
import { MaterialTypeEnum } from "@ns/types";
import { useEffect, useMemo, useState } from "react";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { Phone } from "lucide-react";
import useUserStore from "../stores/user.store.ts";

export const SingePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setMaterialFilter } = useUserStore();

  const [fileUrls, setFileUrls] = useState<string[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ["codebook", id],
    queryFn: () => api.codebook.getCodebookById(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate } = useMutation({
    mutationFn: (url: string) => api.upload.getFileUrl({ url }),
    onSuccess: (response) => {
      setFileUrls((prev) => {
        if (!prev.includes(response.url)) {
          return [...prev, response.url];
        }
        return prev;
      });
    },
  });

  useEffect(() => {
    if (data?.codebookFiles) {
      const uniqueUrls = new Set(fileUrls); // Prevent duplicate requests
      data.codebookFiles.forEach((file) => {
        if (!uniqueUrls.has(file.file.url)) {
          mutate(file.file.url);
        }
      });
    }
    // eslint-disable
  }, [data, mutate]);

  const images = useMemo(() => {
    return fileUrls?.map((url) => {
      return { original: url, thumbnail: url };
    });
  }, [fileUrls]);

  if (isLoading) {
    return <Loader />;
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <h1 className="text-2xl font-bold mb-4">Proizvod nije pronađen</h1>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Breadcrumb navigation */}
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center text-sm">
          <span
            className="text-gray-600 cursor-pointer"
            onClick={() => {
              // Don't reset material filter when going back to main page
              navigate("/");
            }}
          >
            Početna/
          </span>
          <span
            className="text-gray-600 ml-1 cursor-pointer"
            onClick={() => {
              setMaterialFilter(MaterialTypeEnum.MARBLE);
              navigate("/");
            }}
          >
            {materialTypeReverseMapper(
              data?.codebook?.codebookWarehouse?.type as MaterialTypeEnum,
            )}
            /
          </span>
          <span className="ml-1 bg-[#1e1e1e] text-white px-3 py-1 rounded-[4px]">
            {data?.codebook?.codebookWarehouse?.name}
          </span>
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div>
            {images?.length > 0 ? (
              <ImageGallery
                items={images}
                showPlayButton={false}
                showFullscreenButton={true}
                showNav={true}
                thumbnailPosition="bottom"
                useBrowserFullscreen={true}
              />
            ) : (
              <span className={"text-black"}>
                Nisu dostupne slike materijala
              </span>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            <div>
              <div className="flex flex-row items-center gap-[0.15rem]">
                <span className={"text-black font-bold pb-[0.15rem]"}>+</span>
                <span className="text-black font-bold">
                  {materialTypeReverseMapper(
                    data?.codebook?.codebookWarehouse?.type as MaterialTypeEnum,
                  )}
                </span>
              </div>
              <h1 className="text-3xl text-black font-bold mt-2">
                {data?.codebook?.codebookWarehouse?.name}
              </h1>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-600 mb-1">Šifra materijala</p>
                <div className="border text-black rounded-[8px] p-3">
                  <p>{data?.codebook?.codebookWarehouse?.code}</p>
                </div>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Kontakt</p>
                <a
                  href="tel:+381653744438"
                  className="bg-[#1e1e1e] text-white rounded-[8px] p-3 flex items-center justify-center cursor-pointer hover:bg-[#2a2a2a] transition-colors"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  <span className={"text-[10px] md:text-[16px]"}>
                    +381 65 3744 438
                  </span>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-600 mb-1">Dimenzije</p>
                <div className="border text-black rounded-[8px] p-3">
                  <p>{`${data?.codebook?.height}×${data?.codebook?.width}cm`}</p>
                </div>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Debljina ploče</p>
                <div className="border text-black rounded-[8px] p-3">
                  <p>{`${data?.codebook?.thickness}cm`}</p>
                </div>
              </div>
            </div>

            {/*/!* Collapsible Description *!/*/}
            {/*<div className="border rounded-md overflow-hidden">*/}
            {/*  <button*/}
            {/*    className="w-full p-3 flex justify-between items-center"*/}
            {/*    onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}*/}
            {/*  >*/}
            {/*    <span className="font-medium">Opis</span>*/}
            {/*    {isDescriptionOpen ? (*/}
            {/*      <ChevronUp className="w-5 h-5" />*/}
            {/*    ) : (*/}
            {/*      <ChevronDown className="w-5 h-5" />*/}
            {/*    )}*/}
            {/*  </button>*/}
            {/*  {isDescriptionOpen && (*/}
            {/*    <div className="p-3 border-t">*/}
            {/*      <p>{item.description}</p>*/}
            {/*    </div>*/}
            {/*  )}*/}
            {/*</div>*/}
          </div>
        </div>
      </main>
    </div>
  );
};
